// src/features/tpt_static_data/new.tsx

import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { AutocompleteAsyncField } from "@/features/FormCard/Fields";

import { FormActionsNew } from "@/features/FormCard/Actions";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";

import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";

const client = "instit-quantilia";
const table_name = "tpt_static_data";

// Zod schema for the new form
const FormSchema = z.object({
  // Autocomplete for Strategy
  strategy_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          name: z.string(),
        }),
      ])
      .optional(),
  ),
  strategy_id: z.coerce.number().optional().nullable(),

  // Autocomplete for Time Series File Date
  time_serie_file_date_filename: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          filename: z.string(),
        }),
      ])
      .optional(),
  ),
  time_serie_file_date_id: z.coerce.number().optional().nullable(),
});

export function TptStaticDataNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
    disable_translations_request: true,
  });

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                New tpt_static_data
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="strategy_name"
                    label={t(`${table_name}.columns.strategy_name`, {
                      ns: "tables",
                      defaultValue: "Strategy",
                    })}
                    base_url="etl-quantilia/strategies"
                    column_name="name"
                    id_column="strategy_id"
                    control={form.control}
                  />
                  <AutocompleteAsyncField
                    name="time_serie_file_date_filename"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Time Series File Date",
                    })}
                    base_url="instit-quantilia/time_serie_file_dates"
                    column_name="filename"
                    id_column="time_serie_file_date_id"
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_create")}
          >
            {changedFields
              ? NewDiff(changedFields, [
                  "strategy_id",
                  "time_serie_file_date_id",
                ])
              : "No changes detected"}
          </ConfirmationModal>

          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
